import { useEffect, useMemo, useState } from "react";
import { usePolling } from "../lib/use-polling";
import { api, type DlqItem, type DlqBus } from "../lib/api";
import { busLabel } from "../lib/display-labels";
import { ts } from "./Activity";
import { useConfirm } from "../components/primitives/Confirm";
import { PageHeader } from "../components/primitives/PageHeader";
import { PageHelp } from "../components/PageHelp";
import { InfoTip } from "../components/primitives/InfoTip";
import { Table, type Column } from "../components/primitives/Table";
import { Badge } from "../components/primitives/Badge";
import { Button } from "../components/primitives/Button";
import { Pulse } from "../components/primitives/Pulse";
import { EmptyState } from "../components/primitives/EmptyState";
import { Code } from "../components/primitives/Code";
import { TechnicalDetails } from "../components/primitives/TechnicalDetails";

const PAGE_LIMIT = 200;

/** The badge tone for a stuck event: waiting warns, resent reads as resolved. */
export function statusTone(s: string): "ok" | "warn" | "fail" | "muted" {
  if (s === "pending") return "warn";
  if (s === "refired") return "ok";
  return "muted";
}

/** A stuck event's state in plain words — "Waiting", not `pending`. */
export function stuckStatusLabel(s: string): string {
  switch (s) {
    case "pending": return "Waiting";
    case "refired": return "Resent";
    case "discarded": return "Discarded";
    default: return s;
  }
}

/**
 * Why an event is stuck, in a line.
 *
 * With no recorded reason this says what to do instead of leaving the column
 * blank — an empty cell reads as "no reason", which is a different claim from
 * "the run never got far enough to record one".
 */
export function reasonOf(item: Pick<DlqItem, "failureReason">): string {
  return item.failureReason ?? "No run record — open the event to inspect it";
}

/** Pure, presentational table of stuck events (tested statically). */
export function StuckEventsTable({
  items, selected, onToggle, onRowClick,
}: {
  items: DlqItem[];
  selected: Set<string>;
  onToggle: (id: string) => void;
  onRowClick?: (item: DlqItem) => void;
}) {
  const columns: Column<DlqItem>[] = [
    {
      key: "sel", label: "", width: "32px",
      render: (r) => (
        <input
          type="checkbox"
          aria-label={`select ${r.eventId ?? r.sqsMessageId}`}
          checked={selected.has(r.id)}
          disabled={r.status !== "pending"}
          onClick={(e) => e.stopPropagation()}
          onChange={() => onToggle(r.id)}
        />
      ),
    },
    { key: "bus", label: "WHERE", render: (r) => busLabel(r.bus) },
    {
      key: "event", label: "EVENT", mono: true,
      render: (r) => {
        const id = r.eventId ?? r.sqsMessageId;
        return <span title={id}>{id.length > 18 ? `${id.slice(0, 18)}…` : id}</span>;
      },
    },
    {
      key: "reason", label: "REASON",
      render: (r) => <span className="run-reason" title={reasonOf(r)}>{reasonOf(r).slice(0, 60)}</span>,
    },
    { key: "rc", label: "TRIES", mono: true, render: (r) => (r.receiveCount != null ? String(r.receiveCount) : "—") },
    { key: "drained", label: "SINCE", mono: true, render: (r) => ts(r.drainedAt) },
    { key: "status", label: "STATUS", render: (r) => <Badge tone={statusTone(r.status)}>{stuckStatusLabel(r.status)}</Badge> },
  ];
  return (
    <Table
      columns={columns}
      rows={items}
      rowKey={(r) => r.id}
      onRowClick={onRowClick}
      empty={<EmptyState title="Nothing stuck." />}
    />
  );
}

/** One labelled line in the detail drawer. */
function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="drawer__field">
      <div className="drawer__field-label">{label}</div>
      <div className="drawer__field-value">{value}</div>
    </div>
  );
}

/** Right-side detail drawer for one stuck event. Closes on the Close button,
 *  the backdrop, or Escape. */
export function StuckEventDrawer({ item, onClose }: { item: DlqItem; onClose: () => void }) {
  useEffect(() => {
    /** Escape closes the drawer from anywhere on the page. */
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const payload = JSON.stringify(item.body, null, 2);

  return (
    <div className="drawer" role="dialog" aria-label="stuck event detail">
      <div className="drawer__backdrop" onClick={onClose} />
      <aside className="drawer__panel">
        <div className="drawer__head">
          <span className="uppercase-label">STUCK EVENT</span>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </div>
        <div style={{ marginBottom: 8 }}>
          <Badge tone={statusTone(item.status)}>{stuckStatusLabel(item.status)}</Badge>
        </div>
        <Field label="Where" value={busLabel(item.bus)} />
        <Field label="Reason" value={reasonOf(item)} />
        <Field label="Event id" value={item.eventId ?? "—"} />
        <Field label="Pipeline id" value={item.pipelineId ?? "—"} />
        <Field label="Client id" value={item.organizationId ?? "—"} />
        <Field label="Delivery attempts" value={item.receiveCount != null ? String(item.receiveCount) : "—"} />
        <Field label="Stuck since" value={ts(item.drainedAt)} />
        {item.actionBy && <Field label="Handled by" value={`${item.actionBy} · ${ts(item.actionAt)}`} />}
        <TechnicalDetails title="Message payload" copyText={payload}>
          <Code block>{payload}</Code>
        </TechnicalDetails>
      </aside>
    </div>
  );
}

/**
 * Events that could not be processed and are waiting for a person (spec §6.4).
 *
 * "Resend" puts them back through the pipeline — Tier 1, because it writes to
 * a live destination. "Discard" removes them for good with nothing to undo it,
 * which is what makes it Tier 2 and typed.
 */
export function NeedsAttention() {
  const confirm = useConfirm();
  const [bus, setBus] = useState<string>("");
  const [status, setStatus] = useState<string>("pending");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const summaryQ = usePolling(() => api.dlqSummary(), 15000);
  const itemsQ = usePolling(
    () => api.dlqList({ bus: (bus || undefined) as DlqBus | undefined, status: status || undefined, limit: PAGE_LIMIT }),
    8000,
  );

  const items: DlqItem[] = itemsQ.data?.items ?? [];
  const hasMore = (itemsQ.data?.nextCursor ?? null) != null;
  const buses = summaryQ.data?.buses ?? [];
  const selectedItems = useMemo(() => items.filter((i) => selected.has(i.id)), [items, selected]);

  /** Add or drop one stuck event from the selection. */
  function toggle(id: string) {
    setNote(null);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  /** Select every event still waiting, skipping any already resent or discarded. */
  function selectAllWaiting() {
    setSelected(new Set(items.filter((i) => i.status === "pending").map((i) => i.id)));
  }
  /** Drop the whole selection. */
  function clearSelection() { setSelected(new Set()); }

  /** Check the queues for events that have arrived since the last look, and say
   *  how many turned up. Queues that could not be read are counted separately. */
  async function onSync(target?: DlqBus) {
    setBusy(true); setNote(null);
    try {
      const { results } = await api.dlqSync(target);
      const drained = results.reduce((n, r) => n + r.drained, 0);
      const errs = results.filter((r) => r.error);
      setNote(
        errs.length
          ? `Checked the queues: ${drained} new. ${errs.length} could not be read. Refreshing.`
          : `Checked the queues: ${drained} new. Refreshing.`,
      );
    } finally { setBusy(false); }
  }

  const n = selectedItems.length;
  const noun = n === 1 ? "event" : "events";
  const reasonItems = Array.from(new Set(selectedItems.map(reasonOf))).map((r) => ({ label: r }));

  /** Confirm and put the selected events back through their pipelines. */
  async function onResend() {
    if (n === 0) return;
    const ids = selectedItems.map((i) => i.id);
    const ok = await confirm({
      tier: 1,
      title: `Resend ${n} ${noun}?`,
      body: `Anansi will put ${n === 1 ? "this event" : `these ${n} events`} back through ${n === 1 ? "its pipeline" : "their pipelines"}. Records in the destination may be created or updated.`,
      items: reasonItems,
      confirmLabel: `Resend ${n} ${noun}`,
      onConfirm: async () => {
        const { results } = await api.dlqRefire(ids);
        const okCount = results.filter((r) => r.ok).length;
        const skipped = results.length - okCount;
        setNote(`Resent ${okCount}${skipped ? `, ${skipped} skipped` : ""}. Refreshing.`);
      },
    });
    if (ok) clearSelection();
  }

  /** Confirm and delete the selected events. Tier 2 — there is no recovering them. */
  async function onDiscard() {
    if (n === 0) return;
    const ids = selectedItems.map((i) => i.id);
    const ok = await confirm({
      tier: 2,
      title: `Discard ${n} ${noun}?`,
      body: `${n === 1 ? "This event" : `These ${n} events`} will be deleted and cannot be recovered.`,
      items: reasonItems,
      confirmLabel: `Discard ${n} ${noun}`,
      typedPhrase: "DISCARD",
      onConfirm: async () => {
        const { results } = await api.dlqDiscard(ids);
        const okCount = results.filter((r) => r.ok).length;
        const skipped = results.length - okCount;
        setNote(`Discarded ${okCount}${skipped ? `, ${skipped} skipped` : ""}. Refreshing.`);
      },
    });
    if (ok) clearSelection();
  }

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            All clients
            <InfoTip termKey="needsAttention" />
          </>
        }
        title="Needs attention"
        actions={
          <Button variant="secondary" disabled={busy} onClick={() => void onSync((bus || undefined) as DlqBus | undefined)}>
            {busy ? "Checking…" : bus ? `Check ${busLabel(bus)}` : "Check for new"}
          </Button>
        }
      />
      <PageHelp />

      <div className="attention-tiles">
        {buses.map((b) => (
          <button
            key={b.bus}
            type="button"
            className={`attention-tile${bus === b.bus ? " attention-tile--active" : ""}`}
            aria-pressed={bus === b.bus}
            onClick={() => { setBus(bus === b.bus ? "" : b.bus); clearSelection(); }}
          >
            <div className="attention-tile__label">{busLabel(b.bus)}</div>
            <div className="attention-tile__count mono">
              {b.pending} waiting{b.sqsDepth ? ` · ${b.sqsDepth} unread` : ""}
            </div>
          </button>
        ))}
      </div>

      <div className="runs-filters">
        <label className="runs-filter">
          <span className="uppercase-label">Where</span>
          <select className="field__input" aria-label="Where" value={bus} onChange={(e) => { setBus(e.target.value); clearSelection(); }}>
            <option value="">Everywhere</option>
            {buses.map((b) => <option key={b.bus} value={b.bus}>{busLabel(b.bus)}</option>)}
          </select>
        </label>
        <label className="runs-filter">
          <span className="uppercase-label">Status</span>
          <select className="field__input" aria-label="Status" value={status} onChange={(e) => { setStatus(e.target.value); clearSelection(); }}>
            <option value="pending">Waiting</option>
            <option value="refired">Resent</option>
            <option value="discarded">Discarded</option>
            <option value="">All</option>
          </select>
        </label>
      </div>

      <div className="runs-bulkbar">
        <div className="runs-bulkbar__left">
          {n > 0 ? (
            <>
              <Button disabled={busy} onClick={() => void onResend()}>{`Resend ${n} selected`}</Button>
              <Button variant="danger" disabled={busy} onClick={() => void onDiscard()}>Discard</Button>
              <Button variant="ghost" disabled={busy} onClick={clearSelection}>Clear</Button>
            </>
          ) : items.some((i) => i.status === "pending") ? (
            <Button variant="ghost" onClick={selectAllWaiting}>Select all waiting</Button>
          ) : (
            <span className="runs-bulkbar__hint">Tick waiting events to resend or discard them.</span>
          )}
        </div>
        {note ? <span className="runs-bulkbar__result">{note}</span> : null}
      </div>

      {itemsQ.status === "loading" ? (
        <Pulse />
      ) : (
        <StuckEventsTable
          items={items}
          selected={selected}
          onToggle={toggle}
          onRowClick={(r) => setExpandedId(expandedId === r.id ? null : r.id)}
        />
      )}

      {hasMore && (
        <p className="page-help" style={{ marginTop: 12 }}>
          Showing the first {PAGE_LIMIT}. Narrow by place or status to see the rest.
        </p>
      )}

      {expandedId && items.find((i) => i.id === expandedId) && (
        <StuckEventDrawer item={items.find((i) => i.id === expandedId)!} onClose={() => setExpandedId(null)} />
      )}
    </div>
  );
}
